import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex } from "theme-ui";
import { Hub } from "aws-amplify";
import logo from "../../images/goodleap-white-orange.svg";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getCognitoGroups,
  isBusinessDevelopmentOnly,
  isPathAllowedForBusinessDevelopment,
  BUSINESS_DEVELOPMENT_ALLOWED_PATHS,
} from "../../utils/cognito-groups";

const ALL_NAV_LINKS = [
  { to: "/partners", text: "Partners" },
  { to: "/requests", text: "Requests" },
  { to: "/create-quicklink", text: "Create QuickLink" },
  { to: "/Links", text: "QuickLinks History" },
  { to: "/pay/generate", text: "Pay Links" },
  { to: "/admin-errors", text: "Error Log" },
  { to: "/admin-error-email-settings", text: "Email Alerts" },
];

const NavigationBar = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [groups, setGroups] = useState(null);

  const loadGroups = useCallback(async ({ forceRefresh = false } = {}) => {
    const next = await getCognitoGroups({ forceRefresh });
    setGroups(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Cached session only — never force-refresh on mount (avoids Cognito rate limits).
      const next = await getCognitoGroups({ forceRefresh: false });
      if (!cancelled) setGroups(next);
    })();

    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      const event = payload?.event;
      // Refresh tokens only right after sign-in so newly assigned groups appear.
      if (event === "signIn" || event === "cognitoHostedUI") {
        loadGroups({ forceRefresh: true });
        return;
      }
      // tokenRefresh already issued new tokens — just re-read, do not refresh again.
      if (event === "tokenRefresh") {
        loadGroups({ forceRefresh: false });
        return;
      }
      if (event === "signOut") {
        setGroups([]);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [loadGroups]);

  const bdOnly = groups != null && isBusinessDevelopmentOnly(groups);

  const navLinks = useMemo(() => {
    if (groups == null) return [];
    if (bdOnly) {
      return ALL_NAV_LINKS.filter((link) =>
        BUSINESS_DEVELOPMENT_ALLOWED_PATHS.includes(link.to)
      );
    }
    return ALL_NAV_LINKS;
  }, [groups, bdOnly]);

  // Block deep-links to other admin pages for BD-only users.
  useEffect(() => {
    if (!bdOnly) return;
    if (!isPathAllowedForBusinessDevelopment(pathname)) {
      navigate("/create-quicklink", { replace: true });
    }
  }, [bdOnly, pathname, navigate]);

  const handleLink = (to) => {
    navigate(`../${to}`, { replace: false });
  };

  const isCurrentPath = (path) => {
    if (path === "/" && pathname === "/") return true;
    if (path !== "/") return pathname.includes(path);
    return false;
  };

  return (
    <>
      <Box
        as="nav"
        aria-label="Main"
        sx={{
          display: "flex",
          alignItems: "center",
          height: 56,
          width: "100%",
          mx: "auto",
          backgroundColor: "#003439",
        }}
      >
        <Flex sx={{ alignItems: "center" }}>
          <Box
            sx={{
              mr: [3, 4],
              bg: "transparent",
              p: 0,
              border: "none",
              cursor: "pointer",
            }}
            onClick={() => {
              if (bdOnly) navigate("/create-quicklink");
            }}
          >
            <h1 sx={{ m: 0, p: 0 }}>
              <img src={logo} alt="GoodLeap" style={styles.logo} />
            </h1>
          </Box>
          {navLinks?.map(({ to, text, hiddenDesktop }) => {
            if (hiddenDesktop) return null;
            return (
              <Box
                as="button"
                variant="text.base"
                key={`${to}${text}`}
                onClick={() => handleLink(to)}
                sx={{
                  color: "text.negative",
                  border: "none",
                  bg: "transparent",
                  borderBottom: "2px solid",
                  borderColor: isCurrentPath(to) ? "logo.icon" : "transparent",
                  textDecoration: "none",
                  fontWeight: 600,
                  py: "2px",
                  cursor: "pointer",
                  mr: 3,
                  ":hover": {
                    textDecoration: "none",
                    borderColor: "secondary.l2",
                  },
                }}
              >
                {text}
              </Box>
            );
          })}
        </Flex>
        <Box sx={{ ml: "auto", color: "text.negative" }}>{children}</Box>
      </Box>
    </>
  );
};

const styles = {
  signOut: {
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "#FF8300",
    color: "white",
    outline: "none",
    fontSize: 18,
    padding: "12px",
  },
  logo: {
    width: 160,
    margin: "0 auto",
    display: "block",
    padding: 20,
  },
};

export default NavigationBar;
